import { useState, useEffect, useCallback, useRef } from 'react';
import { defaultData, exportToJSON, importFromJSON } from '../utils/storage';
import { loadAll, syncData, importAll } from '../lib/dataSync';
import { useAuth } from '../lib/AuthContext';
import {
  readCachedData,
  readCachedSynced,
  writeCachedData,
  writeCachedSynced,
  hasPendingOfflineEdits,
  looksLikeNetworkError
} from '../lib/offlineCache';

// Some errors are transient and self-healing — we don't want to scare the
// user with a red banner for them. The most common culprit is the Web Locks
// API used by supabase-js to coordinate session refresh across tabs/PWA
// instances; when one tab steals the lock, the loser throws an AbortError
// like "Lock was stolen by another request" and supabase retries internally.
const isBenignSyncError = (err) => {
  if (!err) return false;
  const name = err.name || '';
  const msg = (err.message || '').toLowerCase();
  if (name === 'AbortError') return true;
  if (msg.includes('lock was stolen')) return true;
  if (msg.includes('aborted')) return true;
  return false;
};

// Replaces the previous localStorage-backed hook. Now reads & writes Supabase
// while keeping the same component-facing API: { data, isLoaded, updateData,
// exportData, importData }.
//
// All writes are persisted by diffing the previous in-memory snapshot against
// the new one and issuing minimal upserts/deletes per table. Writes are
// debounced/coalesced via a queue so rapid `updateData` calls don't fight.
export const useData = () => {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id || null;
  const [data, setData] = useState(defaultData);
  const [isLoaded, setIsLoaded] = useState(false);
  const [syncError, setSyncError] = useState(null);

  // Holds the latest applied state so we can diff against it. This moves in
  // lockstep with `data` (every setData call also assigns it), which lets
  // updateData compute `next` without a functional setState — keeping all
  // side effects OUT of the React updater (StrictMode double-invokes those).
  const lastSyncedRef = useRef(defaultData);
  // Sequential write queue so concurrent updates don't race.
  const queueRef = useRef(Promise.resolve());
  // Counter of in-flight sync operations. Polling reloads are skipped while
  // this is > 0 so we don't overwrite optimistic local state mid-save.
  const pendingWritesRef = useRef(0);
  // Monotonic counter bumped on every local edit. A polling reload captures
  // it before its fetch and aborts if it changed after — otherwise a write
  // that starts AND finishes while the poll's requests are in flight would
  // be overwritten by the poll's pre-write snapshot.
  const writeSeqRef = useRef(0);
  // Guards against stacking multiple offline replays.
  const replayingRef = useRef(false);

  // Load on user change. Strategy:
  //   1. If we have a cached snapshot for this user, hydrate from it
  //      immediately so the UI is responsive offline.
  //   2. In parallel, fetch fresh data from Supabase. On success, replace
  //      cache + state. On network failure, keep the cached snapshot and
  //      let the `online` listener below retry.
  useEffect(() => {
    let cancelled = false;
    if (authLoading) return;
    if (!userId) {
      setIsLoaded(false);
      setData(defaultData);
      lastSyncedRef.current = defaultData;
      return;
    }
    const uid = userId;

    // 1. Hydrate from cache first.
    const cached = readCachedData(uid);
    const cachedSynced = readCachedSynced(uid);
    if (cached) {
      setData(cached);
      lastSyncedRef.current = cachedSynced || cached;
      setIsLoaded(true);
    } else {
      lastSyncedRef.current = defaultData;
      setIsLoaded(false);
    }
    // Snapshot the baseline so we can tell later whether the user edited
    // anything while the initial load was still in flight.
    const baselineAtHydration = lastSyncedRef.current;

    // 2. Refresh from server.
    loadAll(uid)
      .then((loaded) => {
        if (cancelled) return;
        // Detect pending offline edits BEFORE we touch the on-disk
        // cache. If `cached` differs from `cachedSynced`, the user made
        // local changes that haven't been confirmed by the server yet
        // (typical case: a previous write silently failed because the
        // phone briefly lost network). We must preserve those edits on
        // disk until the replay below has actually pushed them up —
        // otherwise closing the app mid-replay would lose them.
        const hasOfflineEdits =
          cached &&
          cachedSynced &&
          JSON.stringify(cached) !== JSON.stringify(cachedSynced);

        // Detect edits made AFTER cache hydration but BEFORE this load
        // resolved (e.g. App.js's auto-record effect, or a fast user tap).
        // Applying `loaded` in that case would clobber them in the UI and
        // — worse — overwrite cachedData on disk while the edit's sync
        // later bumps cachedSynced, inverting the snapshot pair so the
        // next launch replays a DELETE for the edit.
        const editedSinceHydration =
          pendingWritesRef.current > 0 ||
          lastSyncedRef.current !== baselineAtHydration;

        if (hasOfflineEdits) {
          // Show the user their local state (with offline edits) while
          // the replay runs. setData(loaded) here would briefly hide
          // their entries.
          setData(cached);
          lastSyncedRef.current = cached;
          // Update cachedSynced so we know the new server baseline, but
          // do NOT overwrite cachedData on disk — it still holds the
          // pending offline edits until the replay confirms them.
          writeCachedSynced(uid, loaded);
        } else if (editedSinceHydration) {
          // Don't apply the (already stale) `loaded` snapshot. Instead
          // queue a fresh reload behind the in-flight writes so the state
          // we end up with includes the local edits post-sync.
          queueRef.current = queueRef.current.then(async () => {
            try {
              const fresh = await loadAll(uid);
              if (cancelled || pendingWritesRef.current > 0) return;
              setData(fresh);
              lastSyncedRef.current = fresh;
              writeCachedSynced(uid, fresh);
              writeCachedData(uid, fresh);
            } catch {
              // Network — cache already holds the local edits; the
              // reconnect handler / poll will retry.
            }
          });
        } else {
          setData(loaded);
          lastSyncedRef.current = loaded;
          writeCachedSynced(uid, loaded);
          writeCachedData(uid, loaded);
        }
        setIsLoaded(true);

        if (hasOfflineEdits) {
          pendingWritesRef.current += 1;
          queueRef.current = queueRef.current
            // Diff cachedSynced -> cached to push ONLY the user's
            // genuine offline edits. Diffing against `loaded` would
            // treat changes other devices made as deletions and wipe
            // them.
            .then(() => syncData(uid, cachedSynced, cached))
            .then(async () => {
              // Mark the pushed edits as synced immediately so a crash
              // before the re-pull below doesn't replay them again.
              writeCachedSynced(uid, cached);
              // Re-pull so local state reflects both the user's offline
              // edits AND any changes made elsewhere.
              try {
                const merged = await loadAll(uid);
                if (cancelled) return;
                setData(merged);
                lastSyncedRef.current = merged;
                writeCachedSynced(uid, merged);
                writeCachedData(uid, merged);
              } catch {
                // Reload failed (network). Persist what we know is on
                // the server now (which includes the pushed edits) by
                // writing `cached` to disk — at least the next launch
                // won't think there are still pending edits.
                writeCachedData(uid, cached);
              }
            })
            .catch((err) => {
              // eslint-disable-next-line no-console
              console.warn('[useData] offline replay failed', err);
              // Replay failed (network/RLS/etc). Keep the cachedData on
              // disk as-is so we'll retry on next launch.
            })
            .finally(() => {
              pendingWritesRef.current = Math.max(
                0,
                pendingWritesRef.current - 1
              );
            });
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[useData] loadAll failed', err);
        if (cancelled) return;
        // Network failure with a cache → stay on cache, don't surface
        // a scary error. The online listener will retry.
        if (looksLikeNetworkError(err) && cached) {
          setIsLoaded(true);
          return;
        }
        if (!isBenignSyncError(err)) setSyncError(err.message || 'Failed to load data');
        if (!cached) {
          setData(defaultData);
          lastSyncedRef.current = defaultData;
        }
        setIsLoaded(true);
      });

    return () => {
      cancelled = true;
    };
    // Key on the user *id*, not the user object: supabase emits a fresh user
    // object on every TOKEN_REFRESHED (~hourly), and re-running the whole
    // load/replay dance for those would briefly revert state to the disk
    // cache and re-open the load/edit race for no reason.
  }, [userId, authLoading]);

  // Replay any pending offline edits — defined as the diff between the last
  // successfully-synced snapshot on disk (`cachedSynced`) and the current
  // local state. Crucially we do NOT diff against `fresh` server state,
  // because that would treat any items another device added in the
  // meantime as "deletions we need to push", and wipe them.
  // Used by both the `online` listener and the polling reload (the latter
  // covers flaky-wifi cases where the browser never fires `online` because
  // navigator.onLine claimed true the whole time).
  const replayOfflineEdits = useCallback(() => {
    if (!userId || replayingRef.current) return;
    const uid = userId;
    replayingRef.current = true;
    pendingWritesRef.current += 1;
    queueRef.current = queueRef.current
      .then(async () => {
        const cachedSynced = readCachedSynced(uid);
        const local = lastSyncedRef.current;
        if (
          cachedSynced &&
          JSON.stringify(cachedSynced) !== JSON.stringify(local)
        ) {
          // Push only the user's genuine offline edits.
          await syncData(uid, cachedSynced, local);
          // Mark them synced right away so a failure in the reload below
          // doesn't leave the pending-edits flag set forever.
          writeCachedSynced(uid, local);
          writeCachedData(uid, local);
        }
        // Reload from server so we pick up whatever other devices did.
        const fresh = await loadAll(uid);
        setData(fresh);
        lastSyncedRef.current = fresh;
        writeCachedSynced(uid, fresh);
        writeCachedData(uid, fresh);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[useData] offline replay failed', err);
      })
      .finally(() => {
        replayingRef.current = false;
        pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
      });
  }, [userId]);

  // Reconnect handler: when the browser regains connectivity, replay.
  useEffect(() => {
    if (!userId) return undefined;
    window.addEventListener('online', replayOfflineEdits);
    return () => window.removeEventListener('online', replayOfflineEdits);
  }, [userId, replayOfflineEdits]);

  const updateData = useCallback(
    (updates) => {
      if (!userId) return;
      const uid = userId;
      // lastSyncedRef always mirrors the latest applied state (see its
      // declaration), so we can compute `next` here instead of inside the
      // setData updater — React StrictMode double-invokes updaters, and
      // side effects there (queueing syncs, cache writes) are unsafe.
      const before = lastSyncedRef.current;
      const next = { ...before, ...updates };
      lastSyncedRef.current = next;
      writeSeqRef.current += 1;
      // Cache to localStorage so offline edits survive a reload.
      writeCachedData(uid, next);
      setData(next);
      // Enqueue the actual DB sync.
      pendingWritesRef.current += 1;
      queueRef.current = queueRef.current
        .then(() => syncData(uid, before, next))
        .then(() => {
          // Sync succeeded: bump the lastSynced cache too.
          writeCachedSynced(uid, next);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[useData] syncData failed', err);
          // Network failures are silent — the data lives in the cache and
          // the `online` listener / dirty-aware poll will retry. Other
          // errors still surface.
          if (looksLikeNetworkError(err)) return;
          if (!isBenignSyncError(err)) setSyncError(err.message || 'Failed to save');
        })
        .finally(() => {
          pendingWritesRef.current = Math.max(0, pendingWritesRef.current - 1);
        });
    },
    [userId]
  );

  const exportData = useCallback(() => {
    exportToJSON(data);
  }, [data]);

  const importData = useCallback(
    async (file) => {
      if (!userId) return false;
      try {
        const imported = await importFromJSON(file);
        await importAll(userId, imported);
        // Reload from DB to ensure local state matches what was actually persisted.
        const fresh = await loadAll(userId);
        setData(fresh);
        lastSyncedRef.current = fresh;
        writeCachedData(userId, fresh);
        writeCachedSynced(userId, fresh);
        return true;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[useData] import failed', err);
        if (!isBenignSyncError(err)) setSyncError(err.message || 'Import failed');
        return false;
      }
    },
    [userId]
  );

  // Force a reload from Supabase (e.g., after joining a shared calendar).
  const reload = useCallback(async () => {
    if (!userId) return;
    try {
      const fresh = await loadAll(userId);
      setData(fresh);
      lastSyncedRef.current = fresh;
      writeCachedData(userId, fresh);
      writeCachedSynced(userId, fresh);
      return fresh;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[useData] reload failed', err);
      if (!isBenignSyncError(err)) setSyncError(err.message || 'Failed to reload');
      return null;
    }
  }, [userId]);

  // Silent polling reload: never throws, never surfaces errors to the UI,
  // and is a no-op while writes are in flight so we don't stomp optimistic
  // local state with stale server data.
  const silentReload = useCallback(async () => {
    if (!userId) return;
    if (pendingWritesRef.current > 0) return;
    // A previous write never reached the server (silent failure on flaky
    // wifi — navigator.onLine stayed true so the `online` replay never
    // fired). Reloading now would stomp the un-pushed edit and reset the
    // diff baseline past it, losing it permanently. Replay instead; the
    // replay ends with its own fresh reload.
    if (hasPendingOfflineEdits(userId)) {
      replayOfflineEdits();
      return;
    }
    const seq = writeSeqRef.current;
    try {
      const fresh = await loadAll(userId);
      // Re-check after await: a write may have started (or even started AND
      // finished — that's what the seq counter catches) during the request,
      // in which case `fresh` predates it.
      if (pendingWritesRef.current > 0 || writeSeqRef.current !== seq) return;
      setData(fresh);
      lastSyncedRef.current = fresh;
    } catch {
      // swallow — polling should never show an error
    }
  }, [userId, replayOfflineEdits]);

  // Import directly from current localStorage (for one-click migration).
  const importFromLocalStorage = useCallback(async () => {
    if (!userId) return false;
    try {
      const stored = window.localStorage.getItem('life_manager_data');
      if (!stored) throw new Error('No localStorage data found');
      const parsed = JSON.parse(stored);
      await importAll(userId, parsed);
      const fresh = await loadAll(userId);
      setData(fresh);
      lastSyncedRef.current = fresh;
      writeCachedData(userId, fresh);
      writeCachedSynced(userId, fresh);
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[useData] localStorage import failed', err);
      if (!isBenignSyncError(err)) setSyncError(err.message || 'Import failed');
      return false;
    }
  }, [userId]);

  return {
    data,
    isLoaded,
    updateData,
    exportData,
    importData,
    importFromLocalStorage,
    reload,
    silentReload,
    syncError
  };
};
